'use client';

/**
 * CostForecastBurnCard — mode6's full-bleed annual forecast canvas.
 *
 * Calendar-year view: x-axis spans Jan 1 → Dec 31 of the current
 * year. Actuals fill in from Jan 1 to today (substrate-driven);
 * Monte Carlo projection bands cover today → Dec 31. The vertical
 * dashed line marks the actual / projection transition.
 *
 * The historical line is part of the year story, not a redundant
 * report ("how much did I spend last month?" — already known from
 * Google Billing). Year-to-date in front of year-end forecast IS
 * the budget conversation: where we are vs where we'll end up.
 *
 * Three component series (fixed / gemini / vertex) plus a derived
 * total. Each shows P50 as a line, P5–P95 as a soft envelope on the
 * projected portion.
 *
 * KPI tiles below the chart are filter-aware: they sum across the
 * visible non-total components, so toggling fixed/gemini/vertex
 * updates the headline numbers immediately.
 *
 * Reads `cost_forecast_burn` via the capability matrix —
 * (category=cost_analytics, action=forecast) — same pattern as every
 * other mode5/mode6 card. Window + horizon are computed from the
 * current date so the year-end target stays Dec 31 as time passes.
 *
 * @see klyde_edge/ui_config/composition/modes/mode6.py
 *      content_key="cost_forecast_burn_canvas"
 * @see klyde_edge/klyde_costs/responses.py — CostForecastBurnResponse
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useMemo, useRef, useState } from 'react';
import {
  Loader2, XCircle, TrendingUp, DollarSign, Cpu, ImageIcon, Users, Download,
} from 'lucide-react';
import {
  Area, ComposedChart, Line, ReferenceLine, ReferenceDot, ResponsiveContainer,
  Tooltip, XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useFinopsLabel, getFinopsLabel } from '@/lib/stores/total-matrix-store';
import { useCardExport } from '@/lib/design/card-export';
import { text } from '@/lib/design/tokens';
import { useFinopsStrategyData } from './use-finops-query';
import { cn } from '@/lib/utils/cn';
import { exportCardAsPng, exportCardAsSvg, todayStamp } from '@/lib/utils/export-chart';
import type {
  CostForecastBurnResponse,
  CostComponentBands,
} from '@/lib/api/types';

// ─────────────────────────────────────────────────────────────────────
// Series styling — colour scheme + icon per component
// ─────────────────────────────────────────────────────────────────────

interface SeriesSpec {
  name: 'fixed' | 'gemini' | 'vertex' | 'total';
  /** i18n key into `finops_labels` — resolved per-render via useFinopsLabel. */
  labelKey: string;
  stroke: string;
  fillOpacity: number;
  icon: typeof DollarSign;
}

// Concrete colour palette — picks distinct hues that read well in
// both light and dark themes. No dependency on a `--chart-N` token
// system that the codebase doesn't currently expose. Labels are
// server-driven via `useFinopsLabel(spec.labelKey)`.
const SERIES: Record<SeriesSpec['name'], SeriesSpec> = {
  fixed:  { name: 'fixed',  labelKey: 'series_fixed_long',  stroke: '#64748b', fillOpacity: 0.18, icon: DollarSign }, // slate
  gemini: { name: 'gemini', labelKey: 'series_gemini_long', stroke: '#6366f1', fillOpacity: 0.20, icon: Cpu },        // indigo
  vertex: { name: 'vertex', labelKey: 'series_vertex_long', stroke: '#f59e0b', fillOpacity: 0.20, icon: ImageIcon },  // amber
  total:  { name: 'total',  labelKey: 'series_total',       stroke: '#10b981', fillOpacity: 0.10, icon: TrendingUp }, // emerald
};

// ─────────────────────────────────────────────────────────────────────
// Card
// ─────────────────────────────────────────────────────────────────────

export function CostForecastBurnCard() {
  const forecastStrategy = useCapabilityStore((s) => s.strategyName('cost_analytics', 'forecast'));

  // Which series to draw bands for. Default: all three components.
  // Total stays a thin overlay line (no band) so it doesn't clutter.
  const [activeSeries, setActiveSeries] = useState<Record<string, boolean>>({
    fixed: true, gemini: true, vertex: true, total: true,
  });

  // Projected user count. Default 500 — projects at-scale economics
  // by default since this card surfaces in Reception (outside
  // visitors care about post-PMF runway, not today's small observed
  // count). Set null to fall back to substrate-observed n_users.
  const [usersProjected, setUsersProjected] = useState<number | null>(500);

  // Cash threshold drawn as a horizontal reference line on the
  // cumulative axis. P5/P50/P95 of the total cumulative band tell us
  // when each percentile band crosses it — that's the "when does cash
  // run out" answer, expressed inside the same chart that shows where
  // spend is going. Default 50_000 — kept in sync with the runway
  // card's default so the three FinOps views show consistent
  // economics across queries.
  const [cashThreshold, setCashThreshold] = useState<number>(50_000);

  // Year-scope params: actuals from Jan 1 → today, projection from
  // tomorrow → Dec 31 of the current year. Recomputed each mount so
  // the horizon target stays the year-end as time passes.
  const { windowDays, horizonDays } = useMemo(() => yearScopeDays(), []);

  // Card-root ref — the card is the export unit, so legend + filter
  // chrome + KPI strip ship with the chart in one snapshot. Export
  // buttons mark themselves `data-export-hidden` to drop out of the
  // capture (they're controls, not content).
  const cardRef = useRef<HTMLDivElement | null>(null);
  const handleExportPng = () => {
    exportCardAsPng(cardRef.current, `klyde-burn-forecast-${todayStamp()}.png`)
      .catch(() => alert(getFinopsLabel('export_failed')));
  };
  const handleExportSvg = () => {
    exportCardAsSvg(cardRef.current, `klyde-burn-forecast-${todayStamp()}.svg`)
      .catch(() => alert(getFinopsLabel('export_failed')));
  };

  // Simulation params — one object, because it's ALSO the query-key
  // tail: the capture envelope records this card's payload under
  // ['strategy-data', <strategy>, simParams].
  const simParams = {
    horizon_days: horizonDays,
    window_days: windowDays,
    n_simulations: 10_000,
    seed: 42,
    ...(usersProjected != null ? { n_users_projected: usersProjected } : {}),
  };
  const simQuery = useFinopsStrategyData<CostForecastBurnResponse>(forecastStrategy, simParams);
  const data = simQuery.data?.success ? simQuery.data.data ?? null : null;
  const error = simQuery.data && !simQuery.data.success
    ? simQuery.data.error || 'Failed to load forecast'
    : simQuery.error
      ? simQuery.error.message ?? 'Network error'
      : null;
  const isLoading = simQuery.isFetching;

  // Build the long-form chart rows: one row per date, columns per
  // (series, channel) where channel is one of historical|p5|p50|p95.
  // Recharts wants a single rows array; null values create breaks.
  const chartData = useMemo(() => buildChartRows(data), [data]);

  // Crossing dates per percentile against the cash threshold. Total
  // cumulative band: P95 (highest spend) crosses earliest, P5 (lowest
  // spend) crosses latest. Returns null when a band doesn't cross
  // within the chart horizon — that's "Beyond Dec 31" in the tile.
  const crossings = useMemo(
    () => computeCrossings(data, cashThreshold),
    [data, cashThreshold],
  );

  // Unit-economics figure — median CHF per user per month at the
  // currently-selected projected user count. Same numbers the chart
  // is showing, just collapsed to one investor-friendly stat.
  const unitEcon = useMemo(() => computeUnitEcon(data), [data]);

  // Empty/loading/error chrome + chart-axis chrome — strings come from
  // finops_labels so the canvas reads cleanly in zh as well as en.
  // All hooks must run before the early returns below (Rules of Hooks).
  const loadingLabel = useFinopsLabel('loading_forecast');
  const emptyLabel = useFinopsLabel('empty_no_cost_data');
  const transitionLabel = useFinopsLabel('forecast_transition_label');
  // Recharts legend/tooltip names — resolved per series upfront so the
  // map below stays a constant lookup, no hooks inside .map().
  const seriesLabel = {
    fixed:  useFinopsLabel(SERIES.fixed.labelKey),
    gemini: useFinopsLabel(SERIES.gemini.labelKey),
    vertex: useFinopsLabel(SERIES.vertex.labelKey),
    total:  useFinopsLabel(SERIES.total.labelKey),
  } as const;

  // Scene-capture registration — WHERE comes from the container's
  // CardPlacementProvider; this declares the WHAT: painted crossing
  // summary, simulation controls as state, and the query_key that lets
  // the capture envelope record the raw MC response.
  useCardExport({
    label: 'Burn forecast',
    content_key: 'cost_forecast_burn_canvas',
    text: data
      ? `Burn forecast vs CHF ${cashThreshold.toLocaleString()}: ` +
        `P50 crossing ${crossings.p50?.date ?? 'beyond horizon'} · ` +
        `range ${crossings.p95?.date ?? '—'} – ${crossings.p5?.date ?? '—'} (P95–P5)` +
        (unitEcon.perUserMonth != null
          ? ` · ~CHF ${unitEcon.perUserMonth.toFixed(2)}/user/month`
          : '')
      : `Burn forecast vs CHF ${cashThreshold.toLocaleString()} — no simulation data`,
    // PAINTED values (capture rule 3) — raw numerics live in the payload.
    json: data
      ? {
          cash_threshold: `CHF ${cashThreshold.toLocaleString()}`,
          crossing_p50: crossings.p50?.date ?? 'beyond horizon',
          crossing_range: `${crossings.p95?.date ?? '—'} – ${crossings.p5?.date ?? '—'}`,
          per_user_month:
            unitEcon.perUserMonth != null ? `CHF ${unitEcon.perUserMonth.toFixed(2)}/user/month` : null,
          simulations: data.n_simulations.toLocaleString(),
          users_projected: String(data.n_users_projected),
        }
      : undefined,
    state: { active_series: activeSeries, cash_threshold: cashThreshold, users_projected: usersProjected },
    status: error ? 'error' : data ? 'loaded' : isLoading ? 'loading' : 'idle',
    error: error ?? undefined,
    // From the hook, not reconstructed (capture rule 1 — no drift).
    query_key: forecastStrategy ? simQuery.queryKey : null,
  });

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        {loadingLabel}
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-destructive">
        <XCircle className="h-6 w-6 mb-2" />
        <p className={text.body}>{error}</p>
      </div>
    );
  }
  if (!data || data.components.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <TrendingUp className="h-8 w-8 mb-2 opacity-40" />
        <p className={text.body}>{emptyLabel}</p>
      </div>
    );
  }

  const transitionDate = data.period_start;

  return (
    <div ref={cardRef} className="flex flex-col h-full p-4 gap-3 overflow-hidden bg-background">
      <Header data={data} onExportPng={handleExportPng} onExportSvg={handleExportSvg} />

      <UnitEconCallout data={data} unitEcon={unitEcon} />

      <SeriesToggles activeSeries={activeSeries} setActiveSeries={setActiveSeries} />

      <CashControl value={cashThreshold} onChange={setCashThreshold} />

      <UserScaleControl
        observed={data.n_users_observed}
        projected={data.n_users_projected}
        selected={usersProjected}
        onChange={setUsersProjected}
      />

      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid strokeOpacity={0.15} vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10 }}
              tickFormatter={monthLabel}
              minTickGap={40}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 10 }}
              tickFormatter={(v: number) => `${v.toFixed(0)}`}
              width={48}
            />
            <Tooltip
              labelFormatter={(d) => String(d)}
              formatter={(v: number, n: string) => [`CHF ${v.toFixed(2)}`, n]}
              contentStyle={{ fontSize: 12 }}
            />
            <ReferenceLine
              x={transitionDate}
              stroke="hsl(var(--muted-foreground))"
              strokeDasharray="3 3"
              label={{ value: transitionLabel, position: 'top', fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            />

            {/* Cash threshold line + crossing dots — the runway answer
                drawn directly into the spend trajectory chart. P95
                cumulative crosses the threshold earliest (highest
                spend), P5 latest. Dots position visually where each
                percentile band hits the cash line; the date labels
                live in the ScalarStrip below for precision. */}
            <ReferenceLine
              y={cashThreshold}
              stroke="#ef4444"
              strokeDasharray="4 4"
              strokeWidth={1.5}
              ifOverflow="extendDomain"
              label={{
                value: `CHF ${cashThreshold.toLocaleString()}`,
                position: 'right',
                fontSize: 10,
                fill: '#ef4444',
              }}
            />
            {crossings.p95 && (
              <ReferenceDot
                x={crossings.p95.date}
                y={cashThreshold}
                r={5}
                fill="#ef4444"
                stroke="hsl(var(--background))"
                strokeWidth={2}
                ifOverflow="extendDomain"
              />
            )}
            {crossings.p50 && (
              <ReferenceDot
                x={crossings.p50.date}
                y={cashThreshold}
                r={5}
                fill="hsl(var(--primary))"
                stroke="hsl(var(--background))"
                strokeWidth={2}
                ifOverflow="extendDomain"
              />
            )}
            {crossings.p5 && (
              <ReferenceDot
                x={crossings.p5.date}
                y={cashThreshold}
                r={5}
                fill="#10b981"
                stroke="hsl(var(--background))"
                strokeWidth={2}
                ifOverflow="extendDomain"
              />
            )}

            {/* P5–P95 envelopes as stacked Area pairs per visible component:
                  invisible base = p5
                  visible delta  = p95 - p5 stacked on it
                Each pair shares its own stackId. Historical days have null
                values so recharts breaks the polygon — band only renders
                over the projected portion. Flat children (no wrapping <g>),
                because recharts only inspects its direct children. */}
            {(['fixed','gemini','vertex'] as const).flatMap((k) => {
              if (!activeSeries[k]) return [];
              const spec = SERIES[k];
              return [
                <Area
                  key={`${k}-base`}
                  type="monotone"
                  dataKey={`${k}_p5`}
                  stackId={`${k}-band`}
                  stroke="none"
                  fill="transparent"
                  isAnimationActive={false}
                  legendType="none"
                />,
                <Area
                  key={`${k}-delta`}
                  type="monotone"
                  dataKey={`${k}_p95_delta`}
                  stackId={`${k}-band`}
                  stroke="none"
                  fill={spec.stroke}
                  fillOpacity={spec.fillOpacity}
                  isAnimationActive={false}
                  legendType="none"
                  name={`${seriesLabel[k]} P5–P95`}
                />,
              ];
            })}

            {/* P50 / actuals line per visible series — single continuous
                Line that uses the merged historical+p50 column. */}
            {(['fixed','gemini','vertex','total'] as const).map((k) => {
              if (!activeSeries[k]) return null;
              const spec = SERIES[k];
              return (
                <Line
                  key={`${k}-line`}
                  type="monotone"
                  dataKey={`${k}_line`}
                  stroke={spec.stroke}
                  strokeWidth={k === 'total' ? 2.5 : 1.5}
                  dot={false}
                  isAnimationActive={false}
                  name={seriesLabel[k]}
                />
              );
            })}

            <Legend wrapperStyle={{ fontSize: 11 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <CrossingStrip crossings={crossings} year={new Date().getFullYear()} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Header — title + provenance line
// ─────────────────────────────────────────────────────────────────────

function Header({
  data,
  onExportPng,
  onExportSvg,
}: {
  data: CostForecastBurnResponse;
  onExportPng: () => void;
  onExportSvg: () => void;
}) {
  const year = new Date().getFullYear();
  const title = useFinopsLabel('forecast_title', { year });
  const exportPngLabel = useFinopsLabel('export_png_label');
  const exportPngTooltip = useFinopsLabel('export_png_tooltip');
  const exportSvgLabel = useFinopsLabel('export_svg_label');
  const exportSvgTooltip = useFinopsLabel('export_svg_tooltip');
  const provenance = useFinopsLabel('forecast_provenance', {
    n_obs: data.n_observations,
    n_sims: data.n_simulations.toLocaleString(),
  });
  const provenanceTechnical = useFinopsLabel('forecast_provenance_technical', {
    seed: data.seed ?? '',
  });
  // Only render the methodology footnote when there's actually
  // something there (seed was passed). Substrate user count moved out
  // of methodology — it's a scaling assumption, not a method detail.
  const provenanceSecondary = data.seed != null ? provenanceTechnical : '';
  const usersTooltip = useFinopsLabel('forecast_users_tooltip');
  const actualsLine = useFinopsLabel('forecast_actuals_window', {
    start: data.window_start ?? '',
    end:   data.window_end   ?? '',
  });
  const forecastLine = useFinopsLabel('forecast_forecast_window', {
    start: data.period_start,
    end:   data.period_end,
  });
  // The "→ projecting N" tail is a separate label so its <strong> wrapping
  // stays in JSX without losing translation.
  const projectingTemplate = useFinopsLabel('forecast_users_projecting', {
    n_projected: '__N__',
  });
  const showProjecting = data.n_users_projected !== data.n_users_observed;
  const [projectingPrefix, projectingSuffix] = projectingTemplate.split('__N__');

  return (
    <div className="flex items-baseline justify-between gap-3">
      <div>
        <div className={cn(text.heading, 'flex items-center gap-2')}>
          <TrendingUp className="h-4 w-4 text-primary" />
          {title}
        </div>
        <div className="text-xs text-foreground/90 mt-0.5">
          {provenance}
          {showProjecting && (
            <span title={usersTooltip}>
              <Users className="inline h-3 w-3 -mt-0.5 ml-1 mr-0.5" />
              {projectingPrefix.trim()}
              <strong className="text-foreground"> {data.n_users_projected} </strong>
              {projectingSuffix.trim()}
            </span>
          )}
        </div>
        {provenanceSecondary && (
          <div className={cn(text.muted, 'mt-0.5')}>{provenanceSecondary}</div>
        )}
      </div>
      <div className={cn(text.muted, 'text-right shrink-0 flex items-start gap-3')}>
        <div>
          {actualsLine}
          <br />
          {forecastLine}
        </div>
        <div className="flex items-center gap-1" data-export-hidden="true">
          <button
            onClick={onExportPng}
            title={exportPngTooltip}
            aria-label={exportPngTooltip}
            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-border bg-card text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors text-[11px]"
          >
            <Download className="h-3 w-3" />
            <span>{exportPngLabel}</span>
          </button>
          <button
            onClick={onExportSvg}
            title={exportSvgTooltip}
            aria-label={exportSvgTooltip}
            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-border bg-card text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors text-[11px]"
          >
            <Download className="h-3 w-3" />
            <span>{exportSvgLabel}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// SeriesToggles — toggle visibility per series
// ─────────────────────────────────────────────────────────────────────

function SeriesToggles({
  activeSeries,
  setActiveSeries,
}: {
  activeSeries: Record<string, boolean>;
  setActiveSeries: (next: Record<string, boolean>) => void;
}) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {(['fixed','gemini','vertex','total'] as const).map((k) => (
        <SeriesToggleChip
          key={k}
          name={k}
          on={!!activeSeries[k]}
          onToggle={() => setActiveSeries({ ...activeSeries, [k]: !activeSeries[k] })}
        />
      ))}
    </div>
  );
}

function SeriesToggleChip({
  name,
  on,
  onToggle,
}: {
  name: 'fixed' | 'gemini' | 'vertex' | 'total';
  on: boolean;
  onToggle: () => void;
}) {
  const spec = SERIES[name];
  const Icon = spec.icon;
  const label = useFinopsLabel(spec.labelKey);
  return (
    <button
      onClick={onToggle}
      className={cn(
        'flex items-center gap-1 text-xs px-2 py-1 rounded border transition-colors',
        on
          ? 'border-border bg-card'
          : 'border-border/40 bg-transparent text-muted-foreground opacity-60',
      )}
      style={on ? { borderColor: spec.stroke, color: spec.stroke } : undefined}
    >
      <Icon className="h-3 w-3" />
      {label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// UserScaleControl — pick projected user count
// ─────────────────────────────────────────────────────────────────────
//
// Substrate-driven chips: each is an integer multiple of the registered
// actor count from the graph (`n_users_observed`). Selecting "1×"
// sends nothing → the server uses the substrate baseline. CLT scaling
// kicks in at every other chip.

function UserScaleControl({
  observed,
  projected,
  selected,
  onChange,
}: {
  observed: number;
  projected: number;
  selected: number | null;
  onChange: (v: number | null) => void;
}) {
  const baseline = Math.max(observed, 1);
  const scaleLabel = useFinopsLabel('scale_label', { observed: baseline });
  const varianceHint = useFinopsLabel('scale_variance_hint');
  const fixedUpliftHint = useFinopsLabel('scale_fixed_uplift_note', { factor: 3 });
  // Absolute user-count ladder. Today's baseline is always present;
  // forward targets only appear if they're above today (so the chips
  // always read as "scale up", never "scale down"). Targets match the
  // 12-month roadmap framing (1 tenant + N users).
  const FORWARD_TARGETS = [50, 100, 500, 1000];
  const targets: Array<{ count: number; value: number | null; isToday: boolean }> = [
    { count: baseline, value: null, isToday: true },
    ...FORWARD_TARGETS.filter((n) => n > baseline).map((n) => ({ count: n, value: n, isToday: false })),
  ];
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-foreground/80 flex items-center gap-1">
        <Users className="h-3 w-3" />
        {scaleLabel}
      </span>
      {targets.map((t) => (
        <UserScaleChip
          key={t.count}
          count={t.count}
          value={t.value}
          baseline={baseline}
          isToday={t.isToday}
          on={(selected ?? null) === t.value}
          onSelect={() => onChange(t.value)}
        />
      ))}
      {projected !== baseline && (
        <span className="text-foreground/70 text-[11px]">
          {varianceHint}{fixedUpliftHint}
        </span>
      )}
    </div>
  );
}

function UserScaleChip({
  count,
  value,
  baseline,
  isToday,
  on,
  onSelect,
}: {
  count: number;
  value: number | null;
  baseline: number;
  isToday: boolean;
  on: boolean;
  onSelect: () => void;
}) {
  // Today's chip carries an explicit "(today)" tag; forward targets
  // are bare numbers — keeps the ladder visually scannable.
  const todayLabel    = useFinopsLabel('scale_chip_today',  { count });
  const forwardLabel  = useFinopsLabel('scale_chip',        { count });
  const label = isToday ? todayLabel : forwardLabel;
  const tooltip = useFinopsLabel('scale_chip_tooltip', { count, baseline });
  return (
    <button
      onClick={onSelect}
      className={cn(
        'px-2 py-1 rounded border transition-colors',
        on
          ? 'border-primary bg-primary/10 text-primary font-medium'
          : 'border-border bg-card text-foreground/80 hover:border-primary/50',
      )}
      title={tooltip}
    >
      {label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// UnitEconCallout — investor-friendly unit-economics headline
// ─────────────────────────────────────────────────────────────────────
//
// "~CHF X / user / month at N users at scale" is the answer-first stat
// for an investor surface. Same numbers the chart is showing, just
// collapsed to one figure. Caveat is part of the detail line so the
// "assumes independent user activity at scale" assumption travels with
// the headline (rather than hiding in microcopy further down).

function UnitEconCallout({
  data,
  unitEcon,
}: {
  data: CostForecastBurnResponse;
  unitEcon: { perUserMonth: number | null };
}) {
  const headline = useFinopsLabel('forecast_unit_econ_headline', {
    per_user_month: unitEcon.perUserMonth != null ? unitEcon.perUserMonth.toFixed(2) : '—',
    n_projected:    data.n_users_projected.toLocaleString(),
  });
  const detail = useFinopsLabel('forecast_unit_econ_detail');
  return (
    <div className="rounded-md border border-primary/40 bg-primary/10 p-3 text-foreground">
      <div className="text-base font-semibold leading-snug">{headline}</div>
      <div className="text-sm text-foreground/80 leading-relaxed mt-1">{detail}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// CashControl — chip strip for picking the cash threshold
// ─────────────────────────────────────────────────────────────────────

function CashControl({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const presets = [50_000, 100_000, 250_000, 500_000, 1_000_000];
  const label = useFinopsLabel('forecast_cash_label');
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-foreground/80">{label}</span>
      {presets.map((p) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          className={cn(
            'flex items-center gap-1 px-2 py-1 rounded border transition-colors',
            value === p
              ? 'border-primary bg-primary/10 text-primary font-medium'
              : 'border-border bg-card text-foreground/80 hover:border-primary/50',
          )}
        >
          <DollarSign className="h-3 w-3" />
          {p.toLocaleString()}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// CrossingStrip — three tiles: P95 (earliest), P50 (median), P5 (latest)
// ─────────────────────────────────────────────────────────────────────
//
// Replaces the previous "By Dec 31 · P5/P50/P95" tiles, which just
// repeated the chart's right-edge values. Crossing dates carry the
// runway answer that's the actual question this chart now exists to
// answer: when does cumulative spend hit your cash line?

function CrossingStrip({
  crossings,
  year,
}: {
  crossings: Crossings;
  year: number;
}) {
  const labelP95 = useFinopsLabel('forecast_cross_p95_label');
  const labelP50 = useFinopsLabel('forecast_cross_p50_label');
  const labelP5  = useFinopsLabel('forecast_cross_p5_label');
  return (
    <div className="grid grid-cols-3 gap-3">
      <CrossingTile label={labelP95} crossing={crossings.p95} year={year} tone="rose" />
      <CrossingTile label={labelP50} crossing={crossings.p50} year={year} tone="primary" />
      <CrossingTile label={labelP5}  crossing={crossings.p5}  year={year} tone="emerald" />
    </div>
  );
}

function CrossingTile({
  label,
  crossing,
  year,
  tone,
}: {
  label: string;
  crossing: { date: string; dayOffset: number } | null;
  year: number;
  tone: 'rose' | 'primary' | 'emerald';
}) {
  const beyondLabel = useFinopsLabel('forecast_cross_beyond_year', { year_end: `Dec 31, ${year}` });
  const beyondHint  = useFinopsLabel('forecast_cross_beyond_hint');
  const dayHint     = useFinopsLabel('forecast_cross_day_hint',    { n: crossing?.dayOffset ?? 0 });
  const toneClass = tone === 'rose'
    ? 'text-rose-600 dark:text-rose-400'
    : tone === 'emerald'
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-primary';
  return (
    <div className="rounded-lg border border-border bg-card/50 p-3">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-foreground/80">{label}</div>
      <div className={cn('text-lg font-semibold tabular-nums mt-0.5', toneClass)}>
        {crossing ? shortDate(crossing.date) : beyondLabel}
      </div>
      <div className="text-[11px] text-foreground/70 mt-0.5">
        {crossing ? dayHint : beyondHint}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Pure helpers — crossing detection + unit economics
// ─────────────────────────────────────────────────────────────────────

interface Crossing {
  date: string;
  dayOffset: number;
}
interface Crossings {
  p5:  Crossing | null;
  p50: Crossing | null;
  p95: Crossing | null;
}

/**
 * For each percentile band of the total cumulative trajectory, find
 * the first projected day where the band crosses (≥) the cash
 * threshold. The line we draw on the chart adds YTD historical
 * cumulative onto the projection's cumulative_* values, so we apply
 * the same offset here for an apples-to-apples comparison.
 *
 * Returns null per band when the threshold isn't crossed within the
 * projection horizon — surfaces as "Beyond Dec 31" in the tile.
 */
function computeCrossings(
  data: CostForecastBurnResponse | null,
  threshold: number,
): Crossings {
  const empty: Crossings = { p5: null, p50: null, p95: null };
  if (!data) return empty;
  const total = data.components.find((c) => c.name === 'total');
  if (!total) return empty;
  const ytd = total.historical.reduce((sum, h) => sum + h.value, 0);
  const findFirst = (key: 'cumulative_p5' | 'cumulative_p50' | 'cumulative_p95'): Crossing | null => {
    for (let i = 0; i < total.projection.length; i++) {
      const p = total.projection[i];
      if (ytd + p[key] >= threshold) return { date: p.date, dayOffset: i + 1 };
    }
    return null;
  };
  return {
    p5:  findFirst('cumulative_p5'),
    p50: findFirst('cumulative_p50'),
    p95: findFirst('cumulative_p95'),
  };
}

/**
 * CHF / active user / month at the projected scale. Median daily P50
 * burn across the projection horizon × ~30 ÷ projected user count.
 * Returns null when the data shape can't yield it cleanly (no total
 * series, no projection, zero users).
 */
function computeUnitEcon(
  data: CostForecastBurnResponse | null,
): { perUserMonth: number | null } {
  if (!data) return { perUserMonth: null };
  const total = data.components.find((c) => c.name === 'total');
  if (!total || total.projection.length === 0) return { perUserMonth: null };
  const n = data.n_users_projected;
  if (!n || n <= 0) return { perUserMonth: null };
  const meanDailyP50 =
    total.projection.reduce((s, p) => s + p.p50, 0) / total.projection.length;
  return { perUserMonth: (meanDailyP50 * 30) / n };
}

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

/**
 * Compute the param window/horizon needed to cover the calendar year:
 *   window_days  = days from Jan 1 of current year to today (inclusive)
 *   horizon_days = days from tomorrow to Dec 31 of current year
 *
 * The BFF subtracts `window_days` from today to seed the Cypher pull,
 * and adds `horizon_days` to today+1 to project the bands. So passing
 * (window_days = days_since_jan1, horizon_days = days_until_dec31)
 * makes the response cover exactly Jan 1 → Dec 31.
 */
function yearScopeDays(): { windowDays: number; horizonDays: number } {
  const now = new Date();
  const year = now.getFullYear();
  // Use UTC to avoid TZ drift when subtracting / comparing dates.
  const todayUTC = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const jan1UTC  = Date.UTC(year, 0, 1);
  const dec31UTC = Date.UTC(year, 11, 31);
  const windowDays  = Math.max(1, Math.round((todayUTC - jan1UTC)  / 86_400_000));
  const horizonDays = Math.max(1, Math.round((dec31UTC - todayUTC) / 86_400_000));
  return { windowDays, horizonDays };
}

function shortDate(iso: string): string {
  // 2026-04-15 → Apr 15
  if (!iso || iso.length < 10) return iso;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function monthLabel(iso: string): string {
  // 2026-04-15 → Apr — month-only ticks for the year-scale x-axis.
  if (!iso || iso.length < 7) return iso;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short' });
}

/**
 * Build the merged long-form rows recharts consumes — *cumulative* view.
 *
 * Per-day daily P5/P50/P95 of a stationary bootstrap is, by construction,
 * approximately constant across the horizon: every projected day is iid
 * with the same historical distribution, so daily medians sit on a flat
 * line. That's not what the MC has to show. Cumulative is where the
 * uncertainty visibly grows: P50 climbs linearly with horizon (mean × N),
 * P5–P95 fans out with √N. Classic forecast-fan visualization.
 *
 * For each date across Jan 1 → Dec 31:
 *   - <series>_line:       cumulative actual on past days, cumulative P50
 *                          on future days (one continuous line —
 *                          projection P50 starts at YTD, not at zero)
 *   - <series>_p5:         cumulative P5 (only on future days)
 *   - <series>_p95_delta:  cumulative (P95 - P5), stacked on top of p5
 *                          to draw the band
 *
 * The projection's cumulative_p* values from the response are
 * "cumulative since start of horizon" — we add YTD per component to
 * lift them onto the year-cumulative axis.
 *
 * Historical days have null band columns → recharts breaks the area
 * polygon and the band only renders over the forecast window.
 */
function buildChartRows(data: CostForecastBurnResponse | null): Array<Record<string, number | string | null>> {
  if (!data) return [];

  const componentByName: Record<string, CostComponentBands> = {};
  for (const c of data.components) componentByName[c.name] = c;

  // Build a sorted union of all dates.
  const dateSet = new Set<string>();
  for (const c of data.components) {
    for (const h of c.historical) dateSet.add(h.date);
    for (const p of c.projection) dateSet.add(p.date);
  }
  const dates = Array.from(dateSet).sort();

  // Pre-compute per-component running cumulative actuals + YTD totals,
  // and index projection cumulatives by date.
  const cumActualsIdx: Record<string, Record<string, number>> = {};
  const projCumIdx:    Record<string, Record<string, { p5: number; p50: number; p95: number }>> = {};
  const ytdByComp:     Record<string, number> = {};
  for (const c of data.components) {
    cumActualsIdx[c.name] = {};
    projCumIdx[c.name]    = {};
    let running = 0;
    for (const h of c.historical) {
      running += h.value;
      cumActualsIdx[c.name][h.date] = running;
    }
    ytdByComp[c.name] = running;
    for (const p of c.projection) {
      projCumIdx[c.name][p.date] = {
        p5:  p.cumulative_p5,
        p50: p.cumulative_p50,
        p95: p.cumulative_p95,
      };
    }
  }

  const rows: Array<Record<string, number | string | null>> = [];
  for (const date of dates) {
    const row: Record<string, number | string | null> = { date };
    for (const cname of Object.keys(componentByName)) {
      const cumActual = cumActualsIdx[cname]?.[date];
      const projCum   = projCumIdx[cname]?.[date];
      const ytd       = ytdByComp[cname] ?? 0;

      if (cumActual != null) {
        // Past day: continuous cumulative-actuals line, no band.
        row[`${cname}_line`]       = cumActual;
        row[`${cname}_p5`]         = null;
        row[`${cname}_p95_delta`]  = null;
      } else if (projCum) {
        // Future day: lift projection cumulative onto the year axis.
        row[`${cname}_line`]       = ytd + projCum.p50;
        row[`${cname}_p5`]         = ytd + projCum.p5;
        row[`${cname}_p95_delta`]  = Math.max(projCum.p95 - projCum.p5, 0);
      } else {
        row[`${cname}_line`]       = null;
        row[`${cname}_p5`]         = null;
        row[`${cname}_p95_delta`]  = null;
      }
    }
    rows.push(row);
  }
  return rows;
}
